/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Container,
  Typography,
  Stack,
  Skeleton,
  Button,
  Grid,
  TextField,
  Card,
} from '@mui/material';
import {
  CopyButton, BackButton, OutlinedIcons, DetailCard, FormSubmissionError, copyToClipboard,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { SalesRecording, TripMatch } from '@marageti/z4-sdk/lib/travel';
import {
  SalesRecordingStatus, SalesRecordingType,
} from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import {
  format, parse, isValid as isValidDate, addYears,
} from 'date-fns';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { DocStatus } from '@marageti/z4-sdk/lib/documents';
import { Link } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Controller, FieldValues, SubmitHandler, useForm,
} from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatCountryCodes, getCurrencyDetailsContent } from '../../../utils/helpers';
import config from '../../../config';
import { formatDateSold } from '../../../utils/sales';
import CommissionInfo from './commission-info';

const { OutlinedExternalLinkIcon, OutlinedLinkIcon } = OutlinedIcons;

const today = new Date();
const maxDate = addYears(new Date(), 10);

type InternalNotesFormProps = {
  defaultInvoiceDate: string;
  defaultNote: string;
  disabled?: boolean;
  onSubmit: SubmitHandler<FieldValues>
  submissionError?: ApiError;
};

const schema = z.object({
  internalNote: z.string(),
  invoiceDate: z.string(),
});

const InternalNotesForm = ({
  defaultInvoiceDate,
  defaultNote,
  disabled = false,
  onSubmit,
  submissionError = undefined,
}: InternalNotesFormProps) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: {
      errors, isSubmitting, isValid, isDirty,
    },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      internalNote: defaultNote,
      invoiceDate: defaultInvoiceDate,
    },
  });

  const handleDateChange = (newDate: Date | null, onChange: (value: string) => void) => {
    if (newDate && isValidDate(newDate)) {
      const formattedDate = newDate ? format(newDate, 'yyyy-MM') : '';
      onChange(formattedDate);
    }
  };

  const handleSubmitForm = async (data: any) => {
    await onSubmit(data);

    reset({
      internalNote: data.internalNote,
      invoiceDate: data.invoiceDate,
    }, {
      keepDirty: false,
      keepIsValid: false,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleSubmitForm)}>
      {submissionError && (
        <FormSubmissionError
          message={submissionError.message}
          errorCode={submissionError.code}
          errorId={submissionError.id}
        />
      )}
      <Controller
        name="invoiceDate"
        control={control}
        defaultValue={defaultInvoiceDate}
        render={({
          field: {
            onChange, onBlur, value, ref, name,
          },
        }) => (
          <DatePicker
            label="Invoice Month"
            minDate={today}
            maxDate={maxDate}
            onChange={(newDate) => handleDateChange(newDate, onChange)}
            value={value ? parse(value, 'yyyy-MM', new Date()) : null}
            views={['month', 'year']}
            readOnly={disabled}
            slotProps={{
              textField: {
                name,
                onBlur,
                inputRef: ref,
                sx: {
                  maxWidth: { desktop: '304px' },
                  width: '100%',
                  mb: 2,
                  ...(disabled && {
                    backgroundColor: '#F0F0F0',
                    '& .MuiInputBase-root.MuiOutlinedInput-root': {
                      backgroundColor: '#F0F0F0',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#C2C5C5',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#C2C5C5',
                      },
                      '& .MuiInputBase-input': {
                        backgroundColor: '#F0F0F0',
                        color: '#4A5152',
                      },
                      '& .MuiInputAdornment-root': {
                        backgroundColor: '#F0F0F0',
                        '& .MuiIconButton-root': {
                          backgroundColor: '#F0F0F0',
                          '& .MuiSvgIcon-root': {
                            color: '#868B8C',
                          },
                        },
                      },
                    },
                  }),
                },
                className: 'custom-datePicker',
                error: !disabled && !!errors.invoiceDate,
                helperText: !disabled && !!errors.invoiceDate ? errors.invoiceDate.message : '',
              },
            }}
          />
        )}
      />
      <TextField
        label="Internal Notes"
        {...register('internalNote')}
        minRows={6}
        maxRows={20}
        multiline
        fullWidth
        variant="outlined"
        error={!!errors.internalNote}
        helperText={errors.internalNote && errors.internalNote.message}
        inputProps={{ readOnly: disabled }}
        sx={{
          background: disabled ? '#F0F0F0' : 'white',
          mt: 2,
          mb: 3,
          ...(disabled && {
            '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
              '& > textarea': {
                backgroundColor: '#F0F0F0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#C2C5C5 !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#C2C5C5',
              },
            },
          }),
        }}
      />
      {!disabled && (
        <LoadingButton
          type="submit"
          variant="contained"
          {...getSubmitButtonProps(isSubmitting, (isValid && isDirty))}
        >
          Save
        </LoadingButton>
      )}
    </form>
  );
};

type ViewProps = {
  countryCodes: CountryCodeMap | undefined;
  docError: ApiError | undefined;
  error: ApiError | undefined;
  isLoading: boolean;
  onClickDownload: () => void;
  onSubmit: SubmitHandler<FieldValues>
  originalSalesRecording: SalesRecording | undefined;
  salesRecording: SalesRecording | undefined;
  submissionError?: ApiError;
  tripMatch: TripMatch | undefined;
};

const SalesRecordingView = ({
  countryCodes,
  docError,
  error,
  isLoading,
  onClickDownload,
  onSubmit,
  originalSalesRecording,
  salesRecording,
  submissionError = undefined,
  tripMatch,
}: ViewProps) => {
  const getDetailCardTitle = (salesRecordingType: SalesRecordingType) => {
    const types = {
      [SalesRecordingType.General]: 'Sales Details',
      [SalesRecordingType.Adjustment]: 'Sales Adjustment',
      [SalesRecordingType.PartnerRepeat]: 'Sales Details',
      [SalesRecordingType.TravelerReferral]: 'Sales Details',
      [SalesRecordingType.CommissionAfterTravel]: 'Sales Details',
      [SalesRecordingType.PlanningFee]: 'Sales Details',
      [SalesRecordingType.RefundCredit]: 'Sales Adjustment',
    };
    return types[salesRecordingType] || '';
  };

  const getPageTitle = (salesRecordingType: SalesRecordingType) => {
    const types = {
      [SalesRecordingType.General]: 'Standard Sale',
      [SalesRecordingType.Adjustment]: 'Sales Adjustment',
      [SalesRecordingType.PartnerRepeat]: 'Repeat Sale',
      [SalesRecordingType.TravelerReferral]: 'Referral Sale',
      [SalesRecordingType.CommissionAfterTravel]: 'Commission After Travel',
      [SalesRecordingType.PlanningFee]: 'Planning Fee',
      [SalesRecordingType.RefundCredit]: 'Refund Credit',
    };
    return types[salesRecordingType] || '';
  };

  const getSalesRecordingCardItems = (salesRec: SalesRecording) => {
    const totalPriceItem = {
      title: salesRec.salesRecordingType === SalesRecordingType.General ? 'Total Trip Price' : 'Changes to Total Trip Price',
      content: getCurrencyDetailsContent(salesRec.price),
    };
    if (salesRec.salesRecordingType === SalesRecordingType.PlanningFee) totalPriceItem.title = 'Amount';
    if (salesRec.salesRecordingType === SalesRecordingType.CommissionAfterTravel) totalPriceItem.title = 'Total Trip Price';

    const commissionItem = {
      title: salesRec.salesRecordingType === SalesRecordingType.General ? 'Commission to Zicasso' : 'Changes to Commission to Zicasso',
      content: getCurrencyDetailsContent(salesRec.commission),
    };
    if (salesRec.salesRecordingType === SalesRecordingType.CommissionAfterTravel) commissionItem.title = 'Commission to Zicasso';
    if (salesRec.salesRecordingType === SalesRecordingType.PlanningFee) commissionItem.title = 'Commission to Zicasso';

    const items = [
      { title: 'Date Recorded', content: salesRec.createdAt ? formatDateSold(salesRec.createdAt) : '' },
      totalPriceItem,
      commissionItem,
      { title: 'Comments', content: salesRec.priceComments },
      {
        title: 'Sales ID',
        content: (
          <>
            {salesRec.referenceId}
            <CopyButton textToCopy={salesRec.referenceId} iconFontSize="small" size="small" />
          </>
        ),
      },
      ((salesRec.document) ? (
        <Button
          disabled={salesRec.document.status !== DocStatus.AvailableStatus}
          onClick={onClickDownload}
          variant="contained"
          sx={{ width: 'fit-content' }}
        >
          Download Attachment
        </Button>
      ) : null),
    ];
    return items;
  };

  return (
    <Container
      maxWidth="lg"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          py: { desktop: 7, mobile: 3 },
        }}
      >
        <BackButton
          aria-label="back to invoices"
          defaultPath="/sales/invoices"
          sx={{ ml: -1, mb: 2 }}
        />
        {isLoading && (
          <>
            <Skeleton height={32} width={200} />
            <Skeleton height={252} width={774} />
            <Skeleton height={480} width={774} />
          </>
        )}
        {error && (
          <Alert severity="error">
            <AlertTitle>There was an issue getting the sales recording</AlertTitle>
            {error.message}
            {error.code && `Code: ${error.code} `}
            {error.id && `ID: ${error.id}`}
          </Alert>
        )}
        {/* Display Sales Recording or Sales Adjustment Card */}
        {(!isLoading && salesRecording && tripMatch && countryCodes) && (
          <>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h2" sx={{ mb: 2 }}>
                {getPageTitle(salesRecording.salesRecordingType)}
              </Typography>
            </Stack>
            {docError && (
              <Alert severity="error">
                <AlertTitle>There was an issue downloading the sales recording</AlertTitle>
                {docError.message}
                {docError.code && `Code: ${docError.code} `}
                {docError.id && `ID: ${docError.id}`}
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <DetailCard
                  title="Trip Info"
                  items={[
                    { title: 'Partner', content: salesRecording.partnerName },
                    { title: 'Traveler', content: tripMatch.travelers[0].fullName },
                    { title: 'Countries', content: formatCountryCodes(tripMatch.countries, countryCodes) },
                    { title: 'When', content: `${format(new Date(tripMatch.departureDate), 'MMM d, yyyy')} (${tripMatch.totalNights} Nights)` },
                    { title: 'Travelers', content: `${tripMatch.numberOfAdults} Adults, ${tripMatch.numberOfChildren} Children` },
                    { title: 'Matched', content: formatDateSold(tripMatch.createdAt) },
                    {
                      title: 'Trip ID',
                      content: (
                        <>
                          {salesRecording.referenceId}
                          <CopyButton textToCopy={salesRecording.referenceId} iconFontSize="small" size="small" />
                        </>
                      ),
                    },
                  ]}
                />
                <Stack direction="row">
                  <Button component={Link} to={`/trip-matches/${tripMatch.id}`} target="_blank" variant="text" size="small" endIcon={<OutlinedExternalLinkIcon />} sx={{ mt: 3, mr: 3 }}>
                    View Trip
                  </Button>
                  <Button onClick={() => copyToClipboard(`${config.PARTNER_URL}/trip-matches/${tripMatch.id}/sales`)} variant="text" size="small" endIcon={<OutlinedLinkIcon />} sx={{ mt: 3 }}>
                    Copy Trip URL
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  <CommissionInfo tripMatch={tripMatch} tripPrice={salesRecording.price} tripCommission={salesRecording.commission} />
                  <DetailCard
                    title={getDetailCardTitle(salesRecording.salesRecordingType)}
                    items={getSalesRecordingCardItems(salesRecording)}
                  />
                  {originalSalesRecording && salesRecording.id !== originalSalesRecording.id && (
                  <DetailCard
                    sx={{ background: '#FFFFFF', border: '1px solid #C2C5C5' }}
                    title={getDetailCardTitle(originalSalesRecording.salesRecordingType)}
                    items={[
                      { title: 'Date Recorded', content: originalSalesRecording.createdAt ? format(new Date(originalSalesRecording.createdAt), 'MMM d, yyyy') : '' },
                      {
                        title: 'Total Trip Price',
                        content: getCurrencyDetailsContent(originalSalesRecording.price),
                      },
                      {
                        title: 'Commission to Zicasso',
                        content: getCurrencyDetailsContent(originalSalesRecording.commission),
                      },
                      { title: 'Comments', content: originalSalesRecording.priceComments },
                      {
                        title: 'Sale ID',
                        content: (
                          <>
                            {originalSalesRecording.referenceId}
                            <CopyButton textToCopy={originalSalesRecording.referenceId} iconFontSize="small" size="small" />
                          </>
                        ),
                      },
                      ((originalSalesRecording.document) ? (
                        <Button
                          disabled={originalSalesRecording.document.status !== DocStatus.AvailableStatus}
                          onClick={onClickDownload}
                          variant="contained"
                          sx={{ width: 'fit-content' }}
                        >
                          Download Attachment
                        </Button>
                      ) : null),
                    ]}
                  />
                  )}
                  <Card sx={{ p: 3 }}>
                    <InternalNotesForm
                      defaultInvoiceDate={salesRecording.invoiceDate || new Date().toISOString()}
                      defaultNote={salesRecording.internalNote}
                      disabled={salesRecording.status === SalesRecordingStatus.InvoicedStatus}
                      onSubmit={onSubmit}
                      submissionError={submissionError}
                    />
                  </Card>
                </Stack>
              </Grid>
            </Grid>
          </>
        )}
        {/* Display repeat / referral card */}
        {(!isLoading && !tripMatch && salesRecording) && (
          <>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h2" sx={{ mb: 2 }}>
                {getPageTitle(salesRecording.salesRecordingType)}
              </Typography>
            </Stack>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <DetailCard
                  title="Trip Info"
                  items={[
                    { title: 'Partner', content: salesRecording.partnerName },
                    { title: 'Traveler Name', content: salesRecording?.travelers?.length > 0 ? salesRecording.travelers[0].fullName : '-' },
                    { title: 'Submitted', content: salesRecording.createdAt ? format(new Date(salesRecording.createdAt), 'MMM d, yyyy') : '' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <DetailCard
                  title={getDetailCardTitle(salesRecording.salesRecordingType)}
                  items={[
                    {
                      title: 'Type',
                      content: salesRecording.salesRecordingType === SalesRecordingType.PartnerRepeat ? 'Repeat' : 'Referral',
                    },
                    {
                      title: 'Total Trip Price',
                      content: getCurrencyDetailsContent(salesRecording.price),
                    },
                    {
                      title: 'Commission to Zicasso',
                      content: getCurrencyDetailsContent(salesRecording.commission),
                    },
                    { title: 'Comments', content: salesRecording.priceComments },
                    {
                      title: 'Sales ID',
                      content: (
                        <>
                          {salesRecording.referenceId}
                          <CopyButton textToCopy={salesRecording.referenceId} iconFontSize="small" size="small" />
                        </>
                      ),
                    },
                    ((salesRecording.document) ? (
                      <Button
                        disabled={salesRecording.document.status !== DocStatus.AvailableStatus}
                        onClick={onClickDownload}
                        variant="contained"
                        sx={{ width: 'fit-content' }}
                      >
                        Download Attachment
                      </Button>
                    ) : null),
                  ]}
                />
                <Card sx={{ p: 3, marginTop: 3 }}>
                  <InternalNotesForm
                    defaultInvoiceDate={salesRecording.invoiceDate || new Date().toISOString()}
                    defaultNote={salesRecording.internalNote}
                    disabled={salesRecording.status === SalesRecordingStatus.InvoicedStatus}
                    onSubmit={onSubmit}
                    submissionError={submissionError}
                  />
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Container>
  );
};

export default SalesRecordingView;
